import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthHeader } from "services/authHeader";
import { RejectedValue, ErrorResponse, MainResponse } from "shared/interfaces/core-interfaces";
import { HolidayCreateDataResponse, HolidayListResponse, HolidayRequest, HolidayResponse, HolidaysBatchRequest, initialHolidayState } from "./holidayInterfaces";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

export const GetActiveHolidays = createAsyncThunk<HolidayListResponse, void, { rejectValue: RejectedValue }>(
    'Slice/HolidaySlice/GetActiveHolidays',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/holidays/all', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as HolidayListResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInactiveHolidays = createAsyncThunk<HolidayListResponse, void, { rejectValue: RejectedValue }>(
    'Slice/HolidaySlice/GetInactiveHolidays',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/holidays/allInactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as HolidayListResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetHolidaysByYear = createAsyncThunk<HolidayListResponse, { year: number, tenantId: string }, { rejectValue: RejectedValue }>(
    'Slice/HolidaySlice/GetHolidaysByYear',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/holidays/getbyyear?year=' + request.year + '&tenantId=' + request.tenantId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as HolidayListResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetHolidaysCreateData = createAsyncThunk<HolidayCreateDataResponse, void, { rejectValue: RejectedValue }>(
    'Slice/HolidaySlice/GetHolidaysCreateData',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/holidays/getcreatedata', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as HolidayCreateDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetHoliday = createAsyncThunk<HolidayResponse, string, { rejectValue: RejectedValue }>(
    'Slice/HolidaySlice/GetHoliday',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/holidays/get?holidayId=' + request, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as HolidayResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateHoliday = createAsyncThunk<MainResponse, HolidayRequest, { rejectValue: RejectedValue }>(
    'Slice/HolidaySlice/CreateHoliday',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/holidays/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetActiveHolidays())
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Holiday created successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateHolidaysBatch = createAsyncThunk<MainResponse, HolidaysBatchRequest, { rejectValue: RejectedValue }>(
    'Slice/HolidaySlice/CreateHolidaysBatch',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/holidays/createmore', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetActiveHolidays())
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Holidays created successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateHoliday = createAsyncThunk<MainResponse, HolidayRequest, { rejectValue: RejectedValue }>(
    'Slice/HolidaySlice/UpdateHoliday',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/holidays/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetActiveHolidays())
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Holiday updated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeactivateHoliday = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/HolidaySlice/DeactivateHoliday',
    async (id, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/holidays/deactivate?holidayId=' + id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(GetActiveHolidays())
            dispatch(GetInactiveHolidays())
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Holiday deactivated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ReactivateHoliday = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/HolidaySlice/ReactivateHoliday',
    async (id, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/holidays/reactivate?holidayId=' + id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(GetActiveHolidays())
            dispatch(GetInactiveHolidays())
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Holiday reactivated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteHoliday = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/HolidaySlice/DeleteHoliday',
    async (id, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/holidays/delete?holidayId=' + id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(GetInactiveHolidays())
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Holiday deleted successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const HolidaySlice = createSlice({
    name: 'HolidaySlice',
    initialState: initialHolidayState,
    reducers: {
        resetHolidaySlice: (state) => {
            state.activeHolidays = [];
            state.inactiveHolidays = [];
            state.createData = null;
        },
        resetHoliday: (state) => {
            state.holiday = null;
        },
    },
    extraReducers(builder) {
        builder.addCase(GetActiveHolidays.fulfilled, (state, action: PayloadAction<HolidayListResponse>) => {
            state.activeHolidays = action.payload.holidays;
        });
        builder.addCase(GetActiveHolidays.rejected, (state, action) => {
            state.activeHolidays = [];
        });
        builder.addCase(GetInactiveHolidays.fulfilled, (state, action: PayloadAction<HolidayListResponse>) => {
            state.inactiveHolidays = action.payload.holidays;
        });
        builder.addCase(GetHolidaysCreateData.fulfilled, (state, action: PayloadAction<HolidayCreateDataResponse>) => {
            state.createData = action.payload;
        });
        builder.addCase(GetHoliday.fulfilled, (state, action: PayloadAction<HolidayResponse>) => {
            state.holiday = action.payload;
        });
    }
});

export const { resetHolidaySlice, resetHoliday } = HolidaySlice.actions;
export default HolidaySlice.reducer;